import {AbstractService} from "@/services/AbstractService";
import {app} from "@/app";
import {ContextRecord} from "@/services/storage/pixel/Context";
import {getLocalStorageJsonItem, parseUri, setLocalStorageJsonItem} from "@/helpers";
import uuid from 'uuid/v4';

export type EventData = {
  event_name: string,
  event_version: number,
  payload: Record<string, any>,
  context: Record<string, any>,
}

export type EventStackItem = {
  id: string,
  event: EventData,
}


export abstract class AbstractShopifyEvent extends AbstractService {

  eventsStackPayload: null|EventStackItem[] = null;

  get eventsStack():EventStackItem[] {
    if(this.eventsStackPayload === null) {
      this.eventsStackPayload = getLocalStorageJsonItem(app.configOption('localStorage.eventsStack.key'), []) as EventStackItem[];
    }
    return this.eventsStackPayload;
  }
  set eventsStack(value: EventStackItem[]) {
    setLocalStorageJsonItem(app.configOption('localStorage.eventsStack.key'), value);
    this.eventsStackPayload = value;
  }
  pushEventToStack(event: EventData) {
    // enforce max stack length
    const maxArrayLength = 10;
    if(this.eventsStack.length >= maxArrayLength) {
      this.eventsStack.splice(0, this.eventsStack.length - (maxArrayLength - 1));
    }
    // push new element
    this.eventsStack.push({
      id: uuid(),
      event: event,
    });
    // save stack to localStorage
    setLocalStorageJsonItem(app.configOption('localStorage.eventsStack.key'), this.eventsStack);
  }
  removeEventFromStack(eventId: string) {
    const eventStackIndex = this.eventsStack.findIndex(item => item.id === eventId);
    if(eventStackIndex === -1) return;
    this.eventsStack.splice(eventStackIndex, 1);
    setLocalStorageJsonItem(app.configOption('localStorage.eventsStack.key'), this.eventsStack);
  }

  async handle(eventPayload: null|Record<any, any> = null, eventContext: null|Record<any, any> = null) {
    app.storage.pixel.persistVisitorIdAsCartAttribute();
  };

  async getEventContext(): Promise<Record<any, any>> {
    await app.storage.pixel.context.collect();
    const contextRecord = app.storage.pixel.context.record;

    return {
      timing: {
        created_at: (new Date()).toISOString(),
      },
      current: contextRecord,
      referer: this.getEventReferer(),
      pixel: {
        id: app.configOption('pixelId'),
      },
      shop: {
        type: 'shopify',
        domain: window.location.hostname,
        shopify: {
          // shopify calls it myshopify_domain, @see: https://shopify.dev/api/admin-rest/2022-04/resources/shop#resource-object
          domain: app.storage.shopify.shop.domain,
        },
      },
      device: {
        user_agent: navigator.userAgent,
      },
      visitor: {
        id: app.storage.pixel.visitorId,
        shopify: {
          customer_id: app.storage.shopify.customer.customerId,
          content_language: app.storage.shopify.customer.contentLanguage,
          currency: app.storage.shopify.customer.currency,
          micro_session_count: app.storage.shopify.customer.microSessionCount,
          micro_session_id: app.storage.shopify.customer.microSessionId,
          visit_token: app.storage.shopify.customer.visitToken,
          uniq_token: app.storage.shopify.customer.uniqToken,
        }
      }
    };
  }

  getEventReferer(): null|Record<string, any> {
    const refererUri = window.document.referrer;
    if(!refererUri || refererUri === '') return null;

    const urlParts = parseUri(refererUri);
    return {
      uri: refererUri,
      url: {
        host: urlParts.host,
        path: urlParts.protocol + "//" + urlParts.authority + urlParts.path,
        query: urlParts.queryKey,
      }
    }
  }



  async pushEventAndSendStack(data: EventData): Promise<void> {
    this.pushEventToStack(data);
    await this.sendEventsStack();
  }

  async sendEventsStack() {
    if(this.eventsStack.length === 0) return;

    const stackItem = this.eventsStack[0];
    await this.sendEvent(stackItem.event).then(response => {
      if (response.ok) {
        this.removeEventFromStack(stackItem.id);
        this.sendEventsStack();
      }
    });
  }

  async sendEvent(data: EventData): Promise<Response> {

    const utmSource = data?.context?.current?.url.query?.utm_source ?? data?.context?.current?.params?.utm_source;
    if(utmSource) {
      if(utmSource.toLowerCase() === 'rp') {
        app.storage.pixel.mute();
      } else {
        app.storage.pixel.unmute();
      }
    }

    if(app.storage.pixel.muted.active) {
      app.debug(`Pixel: Shopify Event ${data.event_name}: MUTED`, data);
      return new Response();
    }

    app.debug(`Pixel: Shopify Event ${data.event_name}: SENDING`, data);
    const url = app.configOption('endpoint');
    return fetch(url, {
      method: 'post',
      headers: {
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data),
    }).then(response => {
      if (response.ok) {
        app.debug(`Pixel: Shopify Event ${data.event_name}: SENT`);
        return response;
      }

      app.debug(`Pixel: Shopify Event ${data.event_name}: ERROR`, {
        status: response.statusText,
      }, 'error');
      return response;
    }).catch(error => {
      app.debug(`Pixel: Shopify Event ${data.event_name}: EXCEPTION`, {
        error: error,
      }, 'error');
      return error;
    });
  }
}
